package com.milta.core.network

import com.milta.BuildKonfig
import com.milta.core.auth.SessionEvent
import com.milta.core.auth.SessionManager
import com.milta.core.auth.TokenStorage
import com.milta.core.observability.ErrorReporting
import com.milta.feature.auth.data.remote.dto.ApiError
import com.milta.feature.auth.data.remote.dto.RefreshRequest
import com.milta.feature.auth.data.remote.dto.TokenResponse
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpCallValidator
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.auth.Auth
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.observer.ResponseObserver
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logging
import io.ktor.http.HttpHeaders
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

val baseUrl: String get() = BuildKonfig.BASE_URL

/**
 * Minimal HTTP client with no authentication — used for public endpoints (e.g. web/route context).
 */
fun buildHttpClient(): HttpClient = createHttpClient().config {
    install(ContentNegotiation) {
        json(Json {
            ignoreUnknownKeys = true
            isLenient = true
        })
    }
    install(Logging) {
        level = if (BuildKonfig.IS_DEV) LogLevel.HEADERS else LogLevel.NONE
    }
}

/**
 * Full HTTP client with bearer-token authentication and automatic token refresh.
 */
fun buildHttpClient(tokenStorage: TokenStorage, sessionManager: SessionManager): HttpClient =
    createHttpClient().config {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }
        install(Logging) {
            // Dev only: headers carry the Bearer token, which must never reach logcat in prod.
            level = if (BuildKonfig.IS_DEV) LogLevel.HEADERS else LogLevel.NONE
            sanitizeHeader { header -> header.equals(HttpHeaders.Authorization, ignoreCase = true) }
        }
        install(HttpCallValidator) {
            // Unexpected transport failures only; "no connection" is not a bug (see ErrorReporting).
            // Returning normally lets Ktor rethrow the original exception to the caller.
            handleResponseExceptionWithRequest { cause, _ -> ErrorReporting.report(cause, source = "http") }
        }
        install(ResponseObserver) {
            // MIL-22: side-channel peek at 403 bodies for the subscription-expired signal.
            // ResponseObserver tees the response content internally — reading it here does NOT
            // consume the body that each *Api.kt's own decodeOrThrow() reads afterward.
            onResponse { response ->
                if (response.status.value >= 500) {
                    ErrorReporting.reportServerError(
                        method = response.call.request.method.value,
                        rawPath = response.call.request.url.encodedPath,
                        status = response.status.value,
                    )
                }
                if (response.status.value == 403) {
                    val error = runCatching { response.body<ApiError>() }.getOrNull()
                    if (error?.code == "error.subscription_expired" && error.role != null && error.scope != null) {
                        sessionManager.emit(SessionEvent.SubscriptionExpired(error.role, error.scope))
                    }
                }
            }
        }
        install(Auth) {
            bearer {
                loadTokens {
                    val access = tokenStorage.getAccessToken() ?: return@loadTokens null
                    val refresh = tokenStorage.getRefreshToken() ?: return@loadTokens null
                    BearerTokens(access, refresh)
                }
                refreshTokens {
                    val refreshToken = tokenStorage.getRefreshToken() ?: run {
                        sessionManager.emit(SessionEvent.SessionExpired)
                        return@refreshTokens null
                    }
                    try {
                        val response: TokenResponse = client.post("$baseUrl/${Endpoints.authRefresh}") {
                            markAsRefreshTokenRequest()
                            contentType(ContentType.Application.Json)
                            setBody(RefreshRequest(refreshToken))
                        }.body()
                        tokenStorage.saveTokens(response.accessToken, response.refreshToken)
                        ErrorReporting.setUserFromAccessToken(response.accessToken)
                        BearerTokens(response.accessToken, response.refreshToken)
                    } catch (_: ResponseException) {
                        // The server actually answered (4xx/5xx) — the refresh token itself was
                        // rejected (invalid/expired/reuse-detected). This is a real auth failure.
                        tokenStorage.clear()
                        ErrorReporting.clearUser()
                        sessionManager.emit(SessionEvent.SessionExpired)
                        null
                    } catch (_: Exception) {
                        // No server response at all: timeout, DNS failure, connection reset...
                        // Transient network issue, not proof the refresh token is invalid. Keep
                        // the session and stored tokens intact — the next connectivity/refresh
                        // cycle (e.g. OfflineSyncViewModel) will simply retry.
                        null
                    }
                }
                sendWithoutRequest { request ->
                    val url = request.url.buildString()
                    !url.contains("/auth/login") &&
                        !url.contains("/auth/refresh") &&
                        !url.contains("/auth/register") &&
                        !url.contains("/auth/otp/")
                }
            }
        }
    }
